import { CacheKey } from '../../../types/cache-keys';
import {
  ARCHITECTURE_CONTAINERS,
  BRIEF_PARENTS,
} from '../../Solutions/ArchitecturePage/architectureSections';

/**
 * The architecture pages' content tables, described once.
 *
 * Every `go_to_market_*` table from `db/models/gtm_content.py` is a flat, ordered list of copy
 * hanging off a go-to-market row (or off one of its rows). They differ only in their columns, so
 * they get one generic panel and one generic dialog driven by these specs rather than seventeen
 * hand-written editors — a new content table becomes a new entry here.
 *
 * Rows are read from the collections the detailed `db/go-to-market/<id>` payload already nests
 * (`rowsProperty`), so opening the editor costs no extra requests; writes go to the table's own
 * endpoint and invalidate the parent.
 */

export type GtmContentFieldType =
  | 'text'
  | 'multiline'
  | 'number'
  | 'boolean'
  | 'select'
  | 'url';

/** Where a select's options come from when they aren't a fixed list. */
export type GtmContentOptionSource = 'vendors' | 'comparisonVendors';

export interface GtmContentFieldOption {
  value: string | number;
  label: string;
}

export interface GtmContentField {
  key: string;
  label: string;
  type: GtmContentFieldType;
  required?: boolean;
  options?: GtmContentFieldOption[];
  optionsFrom?: GtmContentOptionSource;
  helperText?: string;
  minRows?: number;
  /** Half-width on wide dialogs, so short fields pair up. */
  half?: boolean;
}

export interface GtmContentColumn {
  key: string;
  label: string;
}

export interface GtmContentSpec {
  /** Stable id, used as the accordion/panel key. */
  key: string;
  endpoint: string;
  cacheKey: CacheKey;
  /** Panel heading, plural. */
  title: string;
  /** Dialog heading noun, singular. */
  singular: string;
  /** The FK column pointing at the owner. */
  parentField: string;
  /**
   * Where this collection lives on its parent's serialized payload. GTM-level tables use the
   * API's singular `related_query_name`; leaf tables travel under their parent's plural name.
   */
  rowsProperty: string;
  columns: GtmContentColumn[];
  fields: GtmContentField[];
  /** Leaf collections edited inside this spec's dialog. */
  children?: string[];
  /** What the page does with these rows, shown under the panel heading. */
  description?: string;
}

const ORDER_FIELD: GtmContentField = {
  key: 'order',
  label: 'Order',
  type: 'number',
  half: true,
  helperText: 'Low to high. Ties break by id.',
};

/**
 * The placement keys the architecture page recognises, read from the page itself so the two
 * can't drift. A key the page doesn't know renders nothing, which is why this is a select.
 */
const SECTION_KEY_OPTIONS: GtmContentFieldOption[] = [
  ...Object.entries(ARCHITECTURE_CONTAINERS).map(([key, container]) => ({
    value: key,
    label: `${key} — ${container.navLabel} section`,
  })),
  ...Object.entries(BRIEF_PARENTS).map(([key, parent]) => ({
    value: key,
    label: `${key} — card inside ${parent}`,
  })),
];

const LIST_ITEM_KIND_OPTIONS: GtmContentFieldOption[] = [
  { value: 'question', label: 'Qualifying Question' },
  { value: 'listen_for', label: 'What To Listen For' },
  { value: 'challenge', label: 'Common Challenge' },
  { value: 'objection', label: 'Common Objection' },
  { value: 'internal_resource', label: 'Internal Resource' },
];

const COMPETITOR_POINT_KIND_OPTIONS: GtmContentFieldOption[] = [
  { value: 'weakness', label: 'Competitor Weakness' },
  { value: 'cisco_edge', label: "Cisco's Edge" },
];

export const GTM_CONTENT_SPECS: Record<string, GtmContentSpec> = {
  contentSection: {
    key: 'contentSection',
    endpoint: 'db/go-to-market-content-section',
    cacheKey: CacheKey.GtmContentSection,
    title: 'Content Sections',
    singular: 'Content Section',
    parentField: 'go_to_market',
    rowsProperty: 'content_section',
    description:
      'Every section of the page, in order. The page renders exactly the sections stored here — a key it does not recognise renders nothing.',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'key', label: 'Key' },
      { key: 'heading', label: 'Heading' },
    ],
    fields: [
      {
        key: 'key',
        label: 'Placement Key',
        type: 'select',
        required: true,
        options: SECTION_KEY_OPTIONS,
        helperText: 'One section per key, per motion.',
      },
      ORDER_FIELD,
      { key: 'eyebrow', label: 'Eyebrow', type: 'text' },
      {
        key: 'heading',
        label: 'Heading',
        type: 'text',
        helperText: 'In a hero section, "|" splits the plain line from the accented one.',
      },
      { key: 'intro', label: 'Intro', type: 'multiline', minRows: 3 },
    ],
    children: ['contentPoint', 'contentListItem', 'contentSource'],
  },

  contentPoint: {
    key: 'contentPoint',
    endpoint: 'db/go-to-market-content-point',
    cacheKey: CacheKey.GtmContentPoint,
    title: 'Points',
    singular: 'Point',
    parentField: 'content_section',
    rowsProperty: 'points',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'title', label: 'Title' },
    ],
    fields: [
      { key: 'title', label: 'Title', type: 'text', required: true },
      ORDER_FIELD,
      { key: 'description', label: 'Description', type: 'multiline', minRows: 3 },
    ],
  },

  contentListItem: {
    key: 'contentListItem',
    endpoint: 'db/go-to-market-content-list-item',
    cacheKey: CacheKey.GtmContentListItem,
    title: 'List Items',
    singular: 'List Item',
    parentField: 'content_section',
    rowsProperty: 'list_items',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'kind', label: 'Kind' },
      { key: 'text', label: 'Text' },
    ],
    fields: [
      {
        key: 'kind',
        label: 'Kind',
        type: 'select',
        required: true,
        options: LIST_ITEM_KIND_OPTIONS,
      },
      ORDER_FIELD,
      { key: 'text', label: 'Text', type: 'multiline', required: true, minRows: 2 },
      {
        key: 'response',
        label: 'Response',
        type: 'multiline',
        minRows: 2,
        helperText: 'Objections pair a response with the bullet; internal resources use it for the link.',
      },
    ],
  },

  contentSource: {
    key: 'contentSource',
    endpoint: 'db/go-to-market-content-source',
    cacheKey: CacheKey.GtmContentSource,
    title: 'Sources',
    singular: 'Source',
    parentField: 'content_section',
    rowsProperty: 'sources',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'label', label: 'Label' },
    ],
    fields: [
      { key: 'label', label: 'Label', type: 'text', required: true },
      ORDER_FIELD,
      { key: 'url', label: 'URL', type: 'url' },
    ],
  },

  stat: {
    key: 'stat',
    endpoint: 'db/go-to-market-stat',
    cacheKey: CacheKey.GtmStat,
    title: 'Hero Stats',
    singular: 'Stat',
    parentField: 'go_to_market',
    rowsProperty: 'stat',
    description: 'The figure strip under the hero.',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'label', label: 'Label' },
      { key: 'value', label: 'Value' },
    ],
    fields: [
      { key: 'label', label: 'Label', type: 'text', required: true },
      ORDER_FIELD,
      { key: 'value', label: 'Value', type: 'text', required: true, half: true },
      { key: 'prefix', label: 'Prefix', type: 'text', half: true },
      { key: 'suffix', label: 'Suffix', type: 'text', half: true },
    ],
  },

  recognition: {
    key: 'recognition',
    endpoint: 'db/go-to-market-recognition',
    cacheKey: CacheKey.GtmRecognition,
    title: 'Analyst Recognitions',
    singular: 'Recognition',
    parentField: 'go_to_market',
    rowsProperty: 'recognition',
    description: 'Third-party placements shown in the band above the closing CTA.',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'org', label: 'Organization' },
      { key: 'title', label: 'Title' },
    ],
    fields: [
      { key: 'org', label: 'Organization', type: 'text', required: true, half: true },
      ORDER_FIELD,
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'url', label: 'URL', type: 'url' },
    ],
  },

  evaluationStep: {
    key: 'evaluationStep',
    endpoint: 'db/go-to-market-evaluation-step',
    cacheKey: CacheKey.GtmEvaluationStep,
    title: 'Evaluation Steps',
    singular: 'Evaluation Step',
    parentField: 'go_to_market',
    rowsProperty: 'evaluation_step',
    description: 'The technical evaluation timeline. Needs an "eval-process" content section to show.',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'phase', label: 'Phase' },
      { key: 'title', label: 'Title' },
    ],
    fields: [
      { key: 'phase', label: 'Phase', type: 'text', half: true, helperText: 'e.g. "Weeks 2-4".' },
      ORDER_FIELD,
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'description', label: 'Description', type: 'multiline', minRows: 3 },
    ],
  },

  salesStep: {
    key: 'salesStep',
    endpoint: 'db/go-to-market-sales-step',
    cacheKey: CacheKey.GtmSalesStep,
    title: 'Sales Process Steps',
    singular: 'Sales Step',
    parentField: 'go_to_market',
    rowsProperty: 'sales_step',
    description: 'The repeatable motion, one card per step. Needs a "sales-process" content section to show.',
    columns: [
      { key: 'step_number', label: 'Step' },
      { key: 'title', label: 'Title' },
    ],
    fields: [
      { key: 'step_number', label: 'Step Number', type: 'number', half: true },
      ORDER_FIELD,
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'goal', label: 'Goal', type: 'multiline', minRows: 2 },
      { key: 'talk_track', label: 'Talk Track', type: 'multiline', minRows: 3 },
      { key: 'exit_criteria', label: 'Exit Criteria', type: 'multiline', minRows: 2 },
    ],
    children: ['salesStepAction'],
  },

  salesStepAction: {
    key: 'salesStepAction',
    endpoint: 'db/go-to-market-sales-step-action',
    cacheKey: CacheKey.GtmSalesStepAction,
    title: 'Actions',
    singular: 'Action',
    parentField: 'sales_step',
    rowsProperty: 'actions',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'text', label: 'Text' },
    ],
    fields: [
      { key: 'text', label: 'Text', type: 'multiline', required: true, minRows: 2 },
      ORDER_FIELD,
    ],
  },

  competitor: {
    key: 'competitor',
    endpoint: 'db/go-to-market-competitor',
    cacheKey: CacheKey.GtmCompetitor,
    title: 'Competitors',
    singular: 'Competitor',
    parentField: 'go_to_market',
    rowsProperty: 'competitor',
    description: 'Positioning cards. Needs a "competitive" content section to show.',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'display_name', label: 'Name' },
      { key: 'market_share', label: 'Market Share' },
    ],
    fields: [
      { key: 'vendor', label: 'Vendor', type: 'select', optionsFrom: 'vendors' },
      ORDER_FIELD,
      {
        key: 'name',
        label: 'Display Name',
        type: 'text',
        helperText: 'Overrides the vendor name, e.g. "Microsoft (Copilot for Security)".',
      },
      { key: 'market_share', label: 'Market Share', type: 'text', half: true },
    ],
    children: ['competitorPoint'],
  },

  competitorPoint: {
    key: 'competitorPoint',
    endpoint: 'db/go-to-market-competitor-point',
    cacheKey: CacheKey.GtmCompetitorPoint,
    title: 'Positioning Points',
    singular: 'Positioning Point',
    parentField: 'competitor',
    rowsProperty: 'points',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'kind', label: 'Kind' },
      { key: 'text', label: 'Text' },
    ],
    fields: [
      {
        key: 'kind',
        label: 'Kind',
        type: 'select',
        required: true,
        options: COMPETITOR_POINT_KIND_OPTIONS,
      },
      ORDER_FIELD,
      { key: 'text', label: 'Text', type: 'multiline', required: true, minRows: 2 },
    ],
  },

  comparisonVendor: {
    key: 'comparisonVendor',
    endpoint: 'db/go-to-market-comparison-vendor',
    cacheKey: CacheKey.GtmComparisonVendor,
    title: 'Comparison Columns',
    singular: 'Comparison Column',
    parentField: 'go_to_market',
    rowsProperty: 'comparison_vendor',
    description: 'The vendors compared, one table column each. Add these before the capability rows.',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'vendor_name', label: 'Vendor' },
    ],
    fields: [
      {
        key: 'vendor',
        label: 'Vendor',
        type: 'select',
        required: true,
        optionsFrom: 'vendors',
        helperText: 'One column per vendor, per motion.',
      },
      ORDER_FIELD,
    ],
  },

  comparisonFeature: {
    key: 'comparisonFeature',
    endpoint: 'db/go-to-market-comparison-feature',
    cacheKey: CacheKey.GtmComparisonFeature,
    title: 'Comparison Capabilities',
    singular: 'Comparison Capability',
    parentField: 'go_to_market',
    rowsProperty: 'comparison_feature',
    description: 'The capability rows of the comparison table. Each cell is a value below.',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'name', label: 'Capability' },
    ],
    fields: [
      { key: 'name', label: 'Capability', type: 'text', required: true },
      ORDER_FIELD,
    ],
    children: ['comparisonValue'],
  },

  comparisonValue: {
    key: 'comparisonValue',
    endpoint: 'db/go-to-market-comparison-value',
    cacheKey: CacheKey.GtmComparisonValue,
    title: 'Cells',
    singular: 'Cell',
    parentField: 'comparison_feature',
    rowsProperty: 'values',
    columns: [
      { key: 'vendor_name', label: 'Vendor' },
      { key: 'supported', label: 'Supported' },
    ],
    fields: [
      {
        key: 'comparison_vendor',
        label: 'Comparison Column',
        type: 'select',
        required: true,
        optionsFrom: 'comparisonVendors',
        helperText: 'One cell per column, per capability.',
      },
      { key: 'supported', label: 'Supported', type: 'boolean' },
      ORDER_FIELD,
    ],
  },

  lab: {
    key: 'lab',
    endpoint: 'db/go-to-market-lab',
    cacheKey: CacheKey.GtmLab,
    title: 'Labs',
    singular: 'Lab',
    parentField: 'go_to_market',
    rowsProperty: 'lab',
    description: 'Hands-on labs. Needs a "learn" content section to show.',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'title', label: 'Title' },
      { key: 'difficulty', label: 'Difficulty' },
    ],
    fields: [
      { key: 'title', label: 'Title', type: 'text', required: true },
      ORDER_FIELD,
      { key: 'description', label: 'Description', type: 'multiline', minRows: 3 },
      { key: 'duration', label: 'Duration', type: 'text', half: true },
      { key: 'difficulty', label: 'Difficulty', type: 'text', half: true },
      { key: 'status', label: 'Status', type: 'text', half: true },
      { key: 'url', label: 'URL', type: 'url' },
    ],
  },

  demo: {
    key: 'demo',
    endpoint: 'db/go-to-market-demo',
    cacheKey: CacheKey.GtmDemo,
    title: 'Demos',
    singular: 'Demo',
    parentField: 'go_to_market',
    rowsProperty: 'demo',
    description: 'Guided and instant demos. Needs a "learn" content section to show.',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'title', label: 'Title' },
      { key: 'duration', label: 'Duration' },
    ],
    fields: [
      { key: 'title', label: 'Title', type: 'text', required: true },
      ORDER_FIELD,
      { key: 'description', label: 'Description', type: 'multiline', minRows: 3 },
      { key: 'duration', label: 'Duration', type: 'text', half: true },
      { key: 'status', label: 'Status', type: 'text', half: true },
      { key: 'updated_label', label: 'Updated Label', type: 'text', half: true },
      { key: 'url', label: 'URL', type: 'url' },
    ],
  },

  faq: {
    key: 'faq',
    endpoint: 'db/go-to-market-faq',
    cacheKey: CacheKey.GtmFaq,
    title: 'FAQ',
    singular: 'Question',
    parentField: 'go_to_market',
    rowsProperty: 'faq',
    description: 'Needs a "faq" content section to show.',
    columns: [
      { key: 'order', label: 'Order' },
      { key: 'question', label: 'Question' },
    ],
    fields: [
      { key: 'question', label: 'Question', type: 'multiline', required: true, minRows: 2 },
      ORDER_FIELD,
      { key: 'answer', label: 'Answer', type: 'multiline', minRows: 4 },
    ],
  },
};

/** The GTM-level collections, in the order the admin page lists them. */
export const GTM_PAGE_CONTENT_KEYS = [
  'contentSection',
  'stat',
  'recognition',
  'salesStep',
  'evaluationStep',
  'competitor',
  'comparisonVendor',
  'comparisonFeature',
  'lab',
  'demo',
  'faq',
];

export const gtmContentSpec = (key: string): GtmContentSpec => {
  const spec = GTM_CONTENT_SPECS[key];
  if (!spec) throw new Error(`Unknown GTM content spec: ${key}`);
  return spec;
};

/** A row's label for dialog titles and confirmations. */
export const gtmRowLabel = (spec: GtmContentSpec, row: any): string => {
  if (!row) return spec.singular;
  const candidate =
    row.display_name ||
    row.vendor_name ||
    row.heading ||
    row.title ||
    row.name ||
    row.label ||
    row.question ||
    row.key ||
    row.text;
  const label = typeof candidate === 'string' && candidate.trim() ? candidate.trim() : `#${row.id}`;
  return label.length > 60 ? `${label.slice(0, 60)}…` : label;
};
